import { useEffect, useRef, useState } from "react";
import { Flag } from "./Icons";

const languages = [
  { value: "en", name: "English" },
  { value: "pt", name: "Português" },
  { value: "es", name: "Español" },
];

export default function LanguageSelect({ language, setLanguage, label }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const options = useRef([]);
  const selected = languages.findIndex((item) => item.value === language);
  useEffect(() => {
    if (open) options.current[selected]?.focus();
  }, [open, selected]);
  useEffect(() => {
    const outside = (event) => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, []);
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  return (
    <div
      className="language-select"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        className="language-trigger"
        type="button"
        ref={trigger}
        aria-label={label}
        title={languages[selected].name}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="language-options"
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            setOpen(true);
          }
          if (event.key === "Escape") close();
        }}
      >
        <Flag language={language} />
        <span aria-hidden="true">⌄</span>
      </button>
      {open && (
        <div
          className="language-options"
          id="language-options"
          role="listbox"
          aria-label={label}
          onKeyDown={(event) => {
            const current = options.current.indexOf(document.activeElement);
            let next;
            if (event.key === "ArrowDown")
              next = (current + 1) % languages.length;
            if (event.key === "ArrowUp")
              next = (current + languages.length - 1) % languages.length;
            if (event.key === "Home") next = 0;
            if (event.key === "End") next = languages.length - 1;
            if (next !== undefined) {
              event.preventDefault();
              options.current[next]?.focus();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
        >
          {languages.map((item, index) => (
            <button
              type="button"
              role="option"
              aria-selected={language === item.value}
              tabIndex={-1}
              key={item.value}
              ref={(node) => {
                options.current[index] = node;
              }}
              onClick={() => {
                setLanguage(item.value);
                close();
              }}
            >
              <Flag language={item.value} />
              <span>{item.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
